<template>
  <LoadingDiv v-if="!isReady" />

  <template v-else>
    <section class="contract-workspace" v-if="contract">
      <div class="contract-head">
        <div>
          <label class="active teal-text">Contract</label>
          <h5>{{ contract.client }}</h5>
          <p>
            Review the active contract setup for this client before making plan,
            billing, or onboarding decisions.
          </p>
        </div>

        <span class="contract-state" :class="{ pending: contract.status == 0 }">
          <i class="material-icons">{{
            contract.status == 0 ? "schedule" : "check_circle"
          }}</i>
          {{ contract.status == 0 ? "Pending" : "Active" }}
        </span>
      </div>

      <div class="contract-grid">
        <article class="contract-card period-card">
          <label class="active teal-text">{{ cat == "tpa" ? "TPA" : "Policy" }} Year</label>
          <h6>{{ formatDate(contract.start) }} - {{ formatDate(contract.end) }}</h6>
          <p>
            Enrollee activation, invoice periods, and renewal checks are tied to
            this contract window.
          </p>
        </article>

        <article class="contract-card installments-card">
          <div class="card-title-row">
            <div>
              <label class="active teal-text">Payment Installments</label>
              <h6>Billing Schedule</h6>
            </div>
            <i class="material-icons card-title-icon">event_note</i>
          </div>

          <div class="installment-list">
            <span
              class="installment-pill"
              v-for="installment in contract.installments"
              :key="installment.id"
            >
              {{ formatDate(installment.start) }}
            </span>
          </div>
        </article>

        <article class="contract-card plans-card">
          <div class="card-title-row">
            <div>
              <label class="active teal-text">Plans</label>
              <h6>Covered Plans</h6>
            </div>
            <i class="material-icons card-title-icon">assignment</i>
          </div>

          <div class="contract-list">
            <div class="contract-list-item" v-for="plan in contract.plans" :key="plan.id">
              <strong>{{ plan.name }}</strong>
              <span v-if="isCustomized(plan) && cat != 'tpa'">Customized</span>
              <span v-else>{{ cat == "tpa" ? "Reference Plan" : "Standard" }}</span>
            </div>
          </div>
        </article>

        <article class="contract-card premium-card" v-if="cat != 'tpa'">
          <div class="card-title-row">
            <div>
              <label class="active teal-text">Premiums</label>
              <h6>Premium Rates</h6>
            </div>
            <i class="material-icons card-title-icon">payments</i>
          </div>

          <div class="premium-list">
            <div class="premium-plan" v-for="plan in contract.plans" :key="plan.id">
              <div class="premium-plan-title">
                <strong>{{ plan.name }}</strong>
              </div>

              <div class="premium-rate">
                <span>Individual</span>
                <b>NGN {{ premiumAmount(plan, "i") }}</b>
              </div>

              <div class="premium-rate">
                <span>Family</span>
                <b>NGN {{ premiumAmount(plan, "f") }}</b>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>

    <div class="empty-state" v-else>
      <i class="material-icons">assignment_late</i>
      <h6>Contract details are not available for this client yet.</h6>
    </div>
  </template>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import { changeToMoney } from "@/composables/fxLibrary/coreFx";
import getItemsOfItems from "@/composables/getItemsOfItems";
import LoadingDiv from "@/components/LoadingDiv.vue";

export default {
  name: "ContractClientComponent",
  components: { LoadingDiv },
  props: ["cat", "clientId"],
  setup(props) {
    const response = ref(null);
    const isReady = ref(false);
    const error = ref(null);
    const currUrl = ref(null);
    const currHook = ref(null);
    const show = ref(false);

    // get function
    const getItemsFromApi = (table, id) => {
      (async () => {
        const { res, err } = await getItemsOfItems(table, id);

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = err.value;
        }

        // page is ready
        isReady.value = true;
      })();
    };

    // to naira
    const changeToNaira = (val) => {
      // conclude
      const { fx } = changeToMoney(val);
      return fx.value;
    };

    const formatDate = (date) => {
      return new Date(date).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    const isCustomized = (plan) => {
      return !!plan.premiums?.filter((x) => x.scope > 0).length;
    };

    const premiumAmount = (plan, type) => {
      const premium = plan.premiums?.filter((x) => x.type == type)?.[0];
      return changeToNaira(premium?.amount || 0);
    };

    // starter
    onMounted(() => {
      // start from the top
      getStatus();
    });

    // prinipals
    const getStatus = () => {
      // loader
      isReady.value = false;
      // set current Url
      currUrl.value = "operator/client/status/policy/info";
      // set current Hook
      currHook.value = "?";
      // get items from function
      getItemsFromApi(currUrl.value, props.clientId);
    };

    // handle download
    const handleDownload = () => {
      return null;
    };

    // handle show
    const handleShow = (val) => {
      show.value = val;
    };

    // enrollee ction
    const handleView = () => {
      return null;
    };

    // output pages
    const contract = computed(() => {
      if (!response.value) {
        return null;
      } else {
        return response.value;
      }
    });

    return {
      handleShow,
      handleView,
      handleDownload,
      changeToNaira,
      formatDate,
      isCustomized,
      premiumAmount,
      isReady,
      error,
      show,
      contract,
    };
  },
};
</script>

<style scoped>
.contract-workspace {
  margin-top: 8px;
}

.contract-head,
.contract-card,
.empty-state {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  box-shadow: none;
}

.contract-head {
  align-items: flex-start;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  margin-bottom: 14px;
  padding: 24px;
}

.contract-head h5,
.contract-card h6 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.contract-head h5 {
  font-size: 1.18rem;
  font-weight: 650;
  line-height: 1.25;
}

.contract-head p,
.contract-card p {
  color: #607d8b;
  font-size: 0.94rem;
  line-height: 1.55;
  margin: 7px 0 0;
}

.contract-state {
  align-items: center;
  background: #e8f5e9;
  border: 1px solid #d7ead9;
  border-radius: 18px;
  color: #2e7d32;
  display: inline-flex;
  flex-shrink: 0;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 6px;
  padding: 6px 11px;
}

.contract-state.pending {
  background: #fff8e1;
  border-color: #ffecb3;
  color: #ef6c00;
}

.contract-state i {
  font-size: 17px;
}

.contract-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(260px, 0.8fr) minmax(0, 1.2fr);
  align-items: start;
}

.contract-card {
  padding: 24px;
  min-width: 0;
}

.period-card,
.installments-card {
  align-self: start;
}

.period-card,
.installments-card {
  grid-column: 1;
}

.plans-card {
  grid-column: 2;
  grid-row: 1 / span 2;
}

.premium-card {
  grid-column: 1 / -1;
}

.card-title-row {
  align-items: flex-start;
  border-bottom: 1px solid #eeeeee;
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
  padding-bottom: 14px;
}

.card-title-icon {
  color: #90a4ae;
  font-size: 24px;
}

.contract-list,
.premium-list {
  display: grid;
  gap: 10px;
  min-width: 0;
}

.contract-list-item {
  align-items: center;
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 12px 14px;
}

.contract-list-item strong,
.premium-plan-title strong {
  color: #263238;
  font-weight: 700;
}

.contract-list-item span,
.premium-rate span {
  color: #78909c;
  font-size: 0.8rem;
  font-weight: 700;
}

.premium-plan {
  align-items: center;
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(150px, 1.1fr) repeat(2, minmax(120px, 0.75fr));
  min-width: 0;
  padding: 14px;
}

.premium-plan-title,
.premium-rate {
  min-width: 0;
}

.premium-rate {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  padding: 10px 12px;
}

.premium-rate b {
  color: #00695c;
  display: block;
  font-size: 1.02rem;
  overflow-wrap: anywhere;
}

.premium-plan-title strong,
.premium-rate span,
.premium-rate b {
  overflow-wrap: anywhere;
}

.installment-list {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
}

.installment-pill {
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 16px;
  color: #455a64;
  font-size: 0.86rem;
  font-weight: 700;
  padding: 7px 11px;
}

.empty-state {
  color: #607d8b;
  padding: 32px 16px;
  text-align: center;
}

.empty-state i {
  color: #90a4ae;
  font-size: 34px;
}

.empty-state h6 {
  color: #263238;
  font-weight: 700;
  margin: 8px 0 0;
}

@media (max-width: 900px) {
  .contract-grid {
    grid-template-columns: 1fr;
  }

  .period-card,
  .installments-card,
  .plans-card {
    grid-column: auto;
    grid-row: auto;
  }

  .premium-plan {
    align-items: stretch;
    grid-template-columns: 1fr 1fr;
  }

  .premium-plan-title {
    grid-column: 1 / -1;
  }
}

@media (max-width: 620px) {
  .contract-head {
    flex-direction: column;
  }

  .contract-list-item,
  .premium-plan {
    align-items: flex-start;
    grid-template-columns: 1fr;
  }

  .premium-plan-title {
    grid-column: auto;
  }
}
</style>
